import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { useSite } from "@/context/SiteContentContext";
import { Reveal, SectionTag } from "./Reveal";
import { serviceIcons } from "@/i18n/content";
import { SERVICE_IMAGES } from "@/i18n/assets";
import { toAbsolute } from "@/lib/api";

// Bento layout spans (index-based). AI Solutions (index 6) is the hero tile.
const spans = [
  "md:col-span-2",         // 0 hotel
  "md:col-span-2",         // 1 erp
  "md:col-span-3",         // 2 scada
  "md:col-span-3",         // 3 security
  "md:col-span-2",         // 4 web
  "md:col-span-2",         // 5 mobile
  "md:col-span-2 md:row-span-2", // 6 AI (hero)
  "md:col-span-2",         // 7 hosting
];

export default function Services() {
  const { t, lang } = useLang();
  const { settings } = useSite();
  const items = t.services.items.map((s, i) => ({
    t: settings[`service_${i}_title_${lang}`] || s.t,
    d: settings[`service_${i}_desc_${lang}`] || s.d,
    img: toAbsolute(settings[`service_${i}_image`]) || SERVICE_IMAGES[i],
  }));

  return (
    <section id="services" className="relative section-pad" data-testid="services-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="max-w-2xl mb-14">
          <Reveal><SectionTag>{t.services.tag}</SectionTag></Reveal>
          <Reveal delay={0.05}>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mt-5 mb-4">{t.services.title}</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-slate-400 text-base sm:text-lg leading-relaxed">{t.services.subtitle}</p>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-6 gap-5 auto-rows-[minmax(180px,auto)]">
          {items.map((s, i) => {
            const Icon = serviceIcons[i];
            const isHero = i === 7;
            return (
              <Reveal key={i} delay={(i % 3) * 0.08} className={spans[i]}>
                <motion.div
                  whileHover={{ y: -6 }}
                  className="group relative h-full card-surface rounded-3xl overflow-hidden flex flex-col"
                  data-testid={`service-card-${i}`}
                >
                  <Link to={`/services/${i}`} className="relative flex flex-col h-full p-7">
                  <div className={`absolute inset-0 transition-opacity duration-500 ${isHero ? "opacity-30 group-hover:opacity-45" : "opacity-[0.12] group-hover:opacity-25"}`}>
                    <img src={s.img} alt="" loading="lazy" className="h-full w-full object-cover" />
                    <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(10,17,40,0.65), rgba(10,17,40,0.96))" }} />
                  </div>
                  <div className="relative z-10 flex flex-col h-full">
                    <div className="h-12 w-12 rounded-2xl grid place-items-center mb-5 transition-transform duration-300 group-hover:scale-110 group-hover:-rotate-6" style={{ background: "rgba(0,242,96,0.12)" }}>
                      <Icon size={24} className="text-primary" />
                    </div>
                    <h3 className={`font-bold mb-2 ${isHero ? "text-2xl" : "text-lg"}`}>{s.t}</h3>
                    <p className="text-slate-400 text-sm leading-relaxed">{s.d}</p>
                    <span className="mt-auto pt-4 text-xs font-bold tracking-widest uppercase text-primary opacity-0 group-hover:opacity-100 transition-opacity">0{i + 1}</span>
                  </div>
                  </Link>
                </motion.div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
